import { useEffect, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { Hash, MessageSquare, Plus } from "lucide-react";
import { api } from "../../../convex/_generated/api";
import type { Id } from "../../../convex/_generated/dataModel";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (id: Id<"conversations">) => void;
};

export function SearchPalette({ open, onOpenChange, onSelect }: Props) {
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 200);
    return () => clearTimeout(t);
  }, [query]);

  const myList = useQuery(api.conversations.listMine);
  const rooms = useQuery(api.conversations.listRooms);
  const messageHits = useQuery(
    api.search.global,
    debounced ? { query: debounced } : "skip",
  );
  const userHits = useQuery(
    api.users.search,
    debounced ? { query: debounced } : "skip",
  );
  const getOrCreateDm = useMutation(api.conversations.getOrCreateDm);
  const joinRoom = useMutation(api.conversations.joinRoom);

  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const myConversationIds = new Set(
    (myList ?? []).map((i) => i.conversation._id as unknown as string),
  );
  const otherRooms = (rooms ?? []).filter(
    (r) => !myConversationIds.has(r._id as unknown as string),
  );

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput
        placeholder="Search messages, rooms, users..."
        value={query}
        onValueChange={setQuery}
      />
      <CommandList>
        <CommandEmpty>
          {debounced ? "No results." : "Type to search."}
        </CommandEmpty>

        {messageHits && messageHits.length > 0 ? (
          <>
            <CommandGroup heading="Messages">
              {messageHits.map((m) => (
                <CommandItem
                  key={m._id}
                  value={`msg-${m._id}-${m.body}-${m.conversationLabel}-${m.author?.name ?? ""}`}
                  onSelect={() => onSelect(m.conversationId)}
                >
                  <MessageSquare />
                  <div className="flex flex-col min-w-0">
                    <span className="text-xs text-muted-foreground">
                      {m.conversationLabel} · {m.author?.name ?? "Unknown"}
                    </span>
                    <span className="truncate">{m.body}</span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
          </>
        ) : null}

        {(myList ?? []).length > 0 ? (
          <CommandGroup heading="Your conversations">
            {(myList ?? []).map((item) => (
              <CommandItem
                key={item.conversation._id}
                value={`mine-${item.conversation._id}-${item.conversation.name ?? item.peer?.name ?? ""}`}
                onSelect={() => onSelect(item.conversation._id)}
              >
                {item.conversation.kind === "room" ? (
                  <Hash />
                ) : (
                  <MessageSquare />
                )}
                <span>
                  {item.conversation.kind === "room"
                    ? item.conversation.name
                    : item.peer?.name ?? item.peer?.email}
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        ) : null}

        {otherRooms.length > 0 ? (
          <CommandGroup heading="Discover rooms">
            {otherRooms.map((r) => (
              <CommandItem
                key={r._id}
                value={`room-${r._id}-${r.name}`}
                onSelect={async () => {
                  const id = await joinRoom({ conversationId: r._id });
                  onSelect(id);
                }}
              >
                <Plus />
                <span>Join #{r.name}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        ) : null}

        {userHits && userHits.length > 0 ? (
          <CommandGroup heading="Start a DM">
            {userHits.map((u) => (
              <CommandItem
                key={u._id}
                value={`user-${u._id}-${u.name ?? u.email ?? ""}`}
                onSelect={async () => {
                  const id = await getOrCreateDm({ otherUserId: u._id });
                  onSelect(id);
                }}
              >
                <MessageSquare />
                <span>{u.name ?? u.email}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        ) : null}
      </CommandList>
    </CommandDialog>
  );
}
